import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore';

import { db } from '@/firebase-config';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import type {
  ScriptJobStatus,
  ScriptJobStatusValue,
} from '@/features/sync/script-job-status';
import {
  getFirstScriptJobFromSnapshot,
  toScriptJobStatus,
} from '@/features/sync/script-job-status';
import {
  buildScriptJobCollectionPath,
  buildScriptJobDocPath,
  requireProjectScope,
  type ProjectScope,
} from '@/features/sync/project-scope';
import { logger } from '@/utils/logger';
import type { ScriptJobClient, ScriptJobWithId } from '../contracts/script-job-client';

type ScriptJobSnapshot = {
  empty?: boolean;
  docs: Array<{ id: string; data: () => unknown }>;
};

function buildLatestScriptJobQuery(scope: ProjectScope, userId: string, sourceFile?: string) {
  const constraints: QueryConstraint[] = [
    ...(sourceFile ? [where('sourceFile', '==', sourceFile)] : []),
    where('orgId', '==', scope.orgId),
    where('projectId', '==', scope.projectId),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(1),
  ];

  return query(
    collection(db, buildScriptJobCollectionPath(scope)),
    ...constraints,
  );
}

export class FirebaseScriptJobClient implements ScriptJobClient {
  async getScriptJobStatus(scope: ProjectScope, jobId: string): Promise<ScriptJobStatus | null> {
    const checkedScope = requireProjectScope(scope);
    const jobDoc = await getDoc(doc(db, buildScriptJobDocPath(checkedScope, jobId)));

    if (!jobDoc.exists()) {
      return null;
    }

    return toScriptJobStatus(jobDoc.data());
  }

  subscribeToScriptJob(
    scope: ProjectScope,
    onUpdate: (status: ScriptJobStatusValue, error?: string, job?: ScriptJobStatus & { jobId?: string }) => void,
    jobId?: string,
    sourceFile?: string,
    userIdOverride?: string,
  ): () => void {
    const checkedScope = requireProjectScope(scope);
    const userId = userIdOverride || getEffectiveUserId();
    if (!userId) {
      logger.error('Cannot subscribe to script jobs: user not authenticated');
      return () => {};
    }

    if (jobId) {
      logger.sync(`Subscribing to specific job: ${jobId}`);
      return onSnapshot(doc(db, buildScriptJobDocPath(checkedScope, jobId)), snapshot => {
        if (!snapshot.exists()) return;
        const job = toScriptJobStatus(snapshot.data());
        onUpdate(job.status, job.error, {
          jobId,
          ...job,
        });
      }, error => {
        logger.error('Script job subscription error:', error);
      });
    }

    if (sourceFile) {
      logger.sync(`Subscribing to job for file: ${sourceFile}`);
      return onSnapshot(buildLatestScriptJobQuery(checkedScope, userId, sourceFile), snapshot => {
        const job = getFirstScriptJobFromSnapshot(snapshot as ScriptJobSnapshot);
        if (job) {
          onUpdate(job.status, job.error, job);
        }
      }, error => {
        logger.error('Script job subscription error:', error);
      });
    }

    return onSnapshot(buildLatestScriptJobQuery(checkedScope, userId), snapshot => {
      const job = getFirstScriptJobFromSnapshot(snapshot as ScriptJobSnapshot);
      if (job) {
        onUpdate(job.status, job.error, job);
      }
    }, error => {
      logger.error('Script job subscription error:', error);
    });
  }

  subscribeToLatestScriptJob(
    scope: ProjectScope,
    onUpdate: (job: ScriptJobWithId | null) => void,
    userIdOverride?: string,
  ): () => void {
    const checkedScope = requireProjectScope(scope);
    const userId = userIdOverride || getEffectiveUserId();
    if (!userId) {
      logger.error('Cannot subscribe to script jobs: user not authenticated');
      return () => {};
    }

    return onSnapshot(buildLatestScriptJobQuery(checkedScope, userId), snapshot => {
      onUpdate(getFirstScriptJobFromSnapshot(snapshot as ScriptJobSnapshot));
    }, error => {
      logger.error('Latest script job subscription error:', error);
    });
  }
}
